// #ux-redesign-start: PasswordField primitive (Paso 5 — Auth redesign)
// Variante del Input del DS para passwords:
//   - Toggle de visibilidad (icono ojo / ojo tachado) dentro del control row.
//   - Caps Lock indicator opcional (cuando el usuario está tipeando).
//   - Strength meter opcional (4 niveles, lightweight, sin deps externas).
//
// Pensado para Login (sin meter) y Register (con meter).
//
// PORTABILIDAD: trasplantable a Web-Patients sin cambios.
//
// USO con react-hook-form v5:
//   <PasswordField
//     name="password"
//     label="Passwort"
//     placeholder="••••••••"
//     iconLeft={<LockIcon />}
//     innerRef={register({ required })}
//     error={errors.password?.message}
//     showStrength
//   />
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

let pwIdCounter = 0;
const nextId = () => `ds-pw-${++pwIdCounter}`;

// Heurística simple de "strength" — sin libs externas. Devuelve 0..4.
const scorePassword = (pwd) => {
  if (!pwd || pwd.length < 6) return 0;
  let score = 1;
  const hasUpper = /[A-Z]/.test(pwd);
  const hasNum = /\d/.test(pwd);
  const hasSym = /[^A-Za-z0-9]/.test(pwd);
  if (hasUpper || hasNum) score += 1;
  if (hasUpper && hasNum) score += 1;
  if (hasSym && pwd.length >= 8) score += 1;
  return score;
};

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

EyeIcon.propTypes = { open: PropTypes.bool };

const PasswordField = React.forwardRef(({
  id,
  name,
  label,
  helper,
  error,
  hint,
  size = 'md',
  required = false,
  disabled = false,
  className = '',
  innerRef,
  iconLeft,
  showStrength = false,
  strengthLabels = ['', 'Sehr schwach', 'Schwach', 'Gut', 'Stark'],
  toggleAriaLabel = 'Passwort anzeigen',
  onChange,
  ...rest
}, ref) => {
  const fieldId = React.useMemo(() => id || nextId(), [id]);
  const [visible, setVisible] = useState(false);
  const [score, setScore] = useState(0);
  const [capsLock, setCapsLock] = useState(false);
  const hasError = Boolean(error);
  const helperId = helper || error ? `${fieldId}-helper` : undefined;

  const handleChange = useCallback((e) => {
    if (showStrength) setScore(scorePassword(e.target.value));
    if (onChange) onChange(e);
  }, [onChange, showStrength]);

  const handleKeyUp = useCallback((e) => {
    if (e.getModifierState) setCapsLock(e.getModifierState('CapsLock'));
  }, []);

  const wrapperClasses = [
    'ds-field',
    `ds-field--${size}`,
    'ds-field--has-trailing-action',
    iconLeft ? 'ds-field--has-icon-left' : '',
    hasError ? 'ds-field--error' : '',
    disabled ? 'ds-field--disabled' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={fieldId} className="ds-field__label">
          {label}
          {required && <span className="ds-field__required" aria-hidden="true"> *</span>}
          {hint && <span className="ds-field__hint">{hint}</span>}
        </label>
      )}
      <div className="ds-field__control">
        {iconLeft && <span className="ds-field__icon ds-field__icon--left">{iconLeft}</span>}
        <input
          id={fieldId}
          name={name}
          type={visible ? 'text' : 'password'}
          ref={innerRef || ref}
          disabled={disabled}
          onChange={handleChange}
          onKeyUp={handleKeyUp}
          aria-invalid={hasError || undefined}
          aria-describedby={helperId}
          className="ds-field__input"
          {...rest}
        />
        <button
          type="button"
          className="ds-password__toggle"
          onClick={() => setVisible((v) => !v)}
          aria-label={toggleAriaLabel}
          aria-pressed={visible}
          tabIndex={0}
        >
          <EyeIcon open={!visible} />
        </button>
      </div>
      {capsLock && (
        <div className="ds-password__capslock" role="status">
          ⇪ Caps Lock
        </div>
      )}
      {showStrength && score > 0 && (
        <div className="ds-password__meter" aria-live="polite">
          <div className={`ds-password__meter-bar ds-password__meter-bar--${score}`}>
            <span />
            <span />
            <span />
            <span />
          </div>
          <span className="ds-password__meter-label">{strengthLabels[score]}</span>
        </div>
      )}
      {(error || helper) && (
        <div id={helperId} className={`ds-field__helper ${hasError ? 'ds-field__helper--error' : ''}`}>
          {error || helper}
        </div>
      )}
    </div>
  );
});

PasswordField.displayName = 'PasswordField';

PasswordField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.node,
  helper: PropTypes.node,
  error: PropTypes.node,
  hint: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  innerRef: PropTypes.any,
  iconLeft: PropTypes.node,
  showStrength: PropTypes.bool,
  strengthLabels: PropTypes.arrayOf(PropTypes.string),
  toggleAriaLabel: PropTypes.string,
  onChange: PropTypes.func,
};

export default PasswordField;
// #ux-redesign-end
